import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectAttachmentClaimTypeId } from '../../store/dataSelectors';
import {
    loadClaimEventStatuses,
    loadClaimEventTypes,
    loadDocumentTypeIdsAndNames,
    loadDocumentTypeIdsAndNamesForContract
} from '../../store/directusService';
import {
    clearDocumentAuditData,
    clearDocumentUploadReturnData,
    clearParticipantDocumentDetails,
    deleteFileUploadErrorEntries,
    deleteParticipantDocumentDetail,
    downloadParticipantDocumentById,
    loadDocumentAuditData,
    loadParticipantDocumentDetails,
    onDeleteParticipantDocumentDetail
} from '../../store/documentService';
import {
    clearClaimQueues,
    loadContractConstants,
    searchClaimQueue
} from '../../store/participantService';
import { selectUsersForNames } from '../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../store/userService';
import { formatTime } from '../../utils/dateFunctions';
import { DOCUMENT_ERROR } from '../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../utils/userRoles';
import AuditDocumentModal from '../auditing/auditDocuments/AuditDocumentModal';
import Button from '../formElements/Button';
import Switch from '../formElements/Switch';
import ParticipantStickyMenu from '../navigation/participant/ParticipantStickyMenu';
import DynamicSearch from '../search/DynamicSearch';
import ResultsTable from '../table/ResultsTable';
import LoadingSpinner from '../ui/LoadingSpinner';

import AddDocument from './AddDocument';
import DocumentsTableRow from './DocumentsTableRow';
import EditDocument from './EditDocument';

import '../../commonStyles/antUploaderStyles.css';
import app from '../../app.module.css';
import local from './documents.module.css';

const headCells = [
    { id: 'filename', numeric: false, label: 'Name' },
    { id: 'type', numeric: false, label: 'Document Type' },
    { id: 'dateCreated', numeric: false, label: 'Date Added' },
    { id: 'time', numeric: true, label: 'Time Added' },
    { id: 'userName', numeric: false, label: 'Uploaded by' },
    { id: 'dateModified', numeric: false, label: 'Updated' },
    { id: 'status', numeric: false, label: 'Uploaded Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'dateCreated',
    page: 0,
    rowsPerPage: 10
};
const _PENDING_COUNT = 18;
const WAIT = 20000;
const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const Documents = () => {
    const dispatch = useDispatch();
    const pendingStateTimeoutRef = useRef(null);

    // LOCAL STATE
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [pendingCount, setPendingCount] = useState(0);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [isDisabled, setIsDisabled] = useState(true);
    const [participantId, setParticipantId] = useState('');
    const [searchedUserIds, setSearchedUserIds] = useState([]);

    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const attachmentClaimTypeId = useSelector(selectAttachmentClaimTypeId);
    const users = useSelector(selectUsersForNames);
    const participantDocumentDetails = useSelector(
        (state) => state.entities.documentService.participantDocumentDetails
    );
    const participantDocumentDetailsMetaData = useSelector(
        (state) => state.entities.documentService.participantDocumentDetailsMetaData
    );
    const loadingParticipantDocumentDetails = useSelector(
        (state) => state.entities.documentService.loadingParticipantDocumentDetails
    );
    const participantDocumentErrors = useSelector(
        (state) => state.entities.documentService.participantDocumentErrors
    );
    const documentUploadReturnData = useSelector(
        (state) => state.entities.documentService.documentUploadReturnData
    );
    const documentTypes = useSelector(
        (state) => state.entities.directusService.documentTypeIdsAndNames
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
        if (claimEventTypes?.length < 1) dispatch(loadClaimEventTypes());
        if (claimEventStatuses?.length < 1) dispatch(loadClaimEventStatuses());
        if (documentTypes?.length < 1) dispatch(loadDocumentTypeIdsAndNames());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        cleanUp();
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !currentParticipant?.id ||
            (participantId && currentParticipant.id !== participantId) ||
            claimEventStatuses?.length < 1 ||
            !attachmentClaimTypeId
        )
            return;
        dispatch(loadParticipantDocumentDetails(currentParticipant.id, 0));
        dispatch(loadDocumentTypeIdsAndNamesForContract(currentParticipant.contractId));
        dispatch(loadContractConstants(currentParticipant.contractId));
        const resolutionStatusIds = claimEventStatuses
            .filter(
                (el) =>
                    el.name.toLowerCase() === 'verified' ||
                    el.name.toLowerCase() === 'paid' ||
                    el.name.toLowerCase() === 'claimed' ||
                    el.name.toLowerCase() === 'unclaimable'
            )
            .map((el) => el.id);
        dispatch(
            searchClaimQueue(
                {
                    eventTypeId: attachmentClaimTypeId,
                    statusIds: resolutionStatusIds,
                    ptCodes: [currentParticipant.ptCode]
                },
                0
            )
        );
        setParticipantId(currentParticipant.id);
    }, [currentParticipant, claimEventStatuses, attachmentClaimTypeId]);

    useEffect(() => {
        if (
            !participantDocumentDetailsMetaData.last &&
            participantDocumentDetails?.length > 0 &&
            !loadingParticipantDocumentDetails &&
            'number' in participantDocumentDetailsMetaData
        ) {
            dispatch(
                loadParticipantDocumentDetails(
                    currentParticipant.id,
                    participantDocumentDetailsMetaData.number + 1
                )
            );
        } else if (participantDocumentDetailsMetaData?.last) {
            participantDocumentErrors?.length > 0 && dispatch(deleteFileUploadErrorEntries());
            const documentDetails = participantDocumentDetails.filter(
                (el) => el.participantId === currentParticipant.id
            );
            if (documentDetails?.length < 1) {
                setLoadingContentMessage(DOCUMENT_ERROR);
                rows.length > 0 && setRows([]);
                filteredRows.length > 0 && setFilteredRows([]);
            } else if (documentDetails?.length > 0 && !loadingParticipantDocumentDetails) {
                const isPendingState = documentDetails?.some(
                    (el) => el.status.toUpperCase() === 'PENDING'
                );
                if (isPendingState && pendingCount < _PENDING_COUNT) {
                    pendingStateTimeoutRef.current = setTimeout(() => {
                        dispatch(loadParticipantDocumentDetails(currentParticipant.id, 0));
                        setPendingCount((prev) => prev + 1);
                    }, WAIT);
                }
                if (!isPendingState && pendingCount > 0) setPendingCount(0);

                const missingUserIds = [
                    ...new Set(
                        documentDetails
                            .map((el) => el.userId)
                            .filter((el) => !users.find((entry) => entry.id === el))
                            .filter((el) => !searchedUserIds.includes(el))
                    )
                ].filter((el) => el.toLowerCase() !== 'polaris system');
                if (missingUserIds.length > 0) {
                    setSearchedUserIds((prev) => [...prev, ...missingUserIds]);
                    dispatch(loadUsersMinimalDetails(missingUserIds));
                } else if (users.length > 0 && documentTypes?.length > 0) {
                    const newRows = documentDetails.map((el) => {
                        let userName;
                        if (el.userId.toLowerCase() === 'polaris system') userName = el.userId;
                        else {
                            const user = users.find((entry) => entry.id === el.userId);
                            userName = user ? `${user.firstName} ${user.lastName}` : ' - ';
                        }
                        return {
                            ...el,
                            status: el.status.toUpperCase(),
                            userName,
                            time: formatTime(el.dateCreated)
                        };
                    });
                    setRows(newRows);
                    setFilteredRows(newRows);
                }
                return () => {
                    if (pendingStateTimeoutRef.current) {
                        clearTimeout(pendingStateTimeoutRef.current);
                        pendingStateTimeoutRef.current = null;
                    }
                };
            }
        }
    }, [
        loadingParticipantDocumentDetails,
        participantDocumentDetails,
        participantDocumentDetailsMetaData,
        users,
        documentTypes
    ]);

    useEffect(() => {
        if (documentUploadReturnData?.length > 0) {
            // triggers reload for documentDetails after new doc upload
            dispatch(loadParticipantDocumentDetails(currentParticipant.id, 0));
        }
    }, [documentUploadReturnData]);

    useEffect(() => {
        if (!rows.length) return;
        const newRows = !searchTerm
            ? rows
            : rows.filter(
                  (el) =>
                      el.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      el.type?.toLowerCase().includes(searchTerm.toLowerCase())
              );
        setFilteredRows(newRows);
        setLoadingContentMessage(!newRows.length ? DOCUMENT_ERROR : '');
    }, [searchTerm]);

    useEffect(() => {
        if (successMessage === `${selectedRow.filename} has been deleted`) {
            dispatch(onDeleteParticipantDocumentDetail(selectedRow.id));
            setSelectedRow({});
        }
    }, [successMessage]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(filteredRows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <DocumentsTableRow
                key={el.id}
                row={el}
                roles={roles}
                acceptedRoles={acceptedRoles}
                onEdit={onEdit}
                onDelete={onDelete}
                onDownload={onDownload}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));
    };

    const cleanUp = () => {
        if (pendingStateTimeoutRef.current) {
            clearTimeout(pendingStateTimeoutRef.current);
            pendingStateTimeoutRef.current = null;
        }
        setRows([]);
        setFilteredRows([]);
        setSearchTerm('');
        setSelectedRow({});
        setShowForm(false);
        setFormType('');
        setPendingCount(0);
        setRowMetaData(initialRowMetaData);
        setLoadingContentMessage('');
        dispatch(clearDocumentUploadReturnData());
        dispatch(clearParticipantDocumentDetails());
        dispatch(clearClaimQueues());
    };

    // EVENT HANDLERS
    const onSearch = (term) => setSearchTerm(term);

    const onAdd = () => {
        setFormType('create');
        !showForm && setShowForm(true);
    };
    const onEdit = (e, row) => {
        e.stopPropagation();
        setFormType('edit');
        !showForm && setShowForm(true);
        setSelectedRow(row);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteParticipantDocumentDetail(currentParticipant.id, row.filename));
    };

    const onDownload = (row) => {
        dispatch(downloadParticipantDocumentById(currentParticipant.id, row.id, row.filename));
    };

    const onSearchAuditItems = (documentId, field) => {
        if (documentId) dispatch(loadDocumentAuditData(documentId, field));
        setShowAuditModal(true);
        setAuditLabel(field);
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearDocumentAuditData());
    };

    const onClose = () => setShowForm(false);

    // RENDER
    return (
        <div className={app.withSideBar}>
            <div className={app.pageContainer}>
                <div className={local.content}>
                    <div className={local.headingContent}>
                        <div className={app.mainHeading}>Documents</div>
                        <Switch
                            id={'auditDisplayFields'}
                            label="Show Audit Fields"
                            onOff={true}
                            customClass="auditSwitch"
                            checked={isAuditable}
                            onChange={(e) => setIsAuditable(e.target.checked)}
                        />
                    </div>
                    <div className={local.toolbar}>
                        <DynamicSearch
                            id="documents"
                            search={onSearch}
                            searchTerm={searchTerm}
                            placeholder="Enter Search Term"
                            disabled={isDisabled}
                        />
                    </div>
                    <div>
                        {filteredRows.length < 1 ? (
                            <div className={local.noContent}>
                                <LoadingSpinner content={loadingContentMessage} />
                            </div>
                        ) : (
                            <ResultsTable
                                defaultOrderBy={rowMetaData.orderBy}
                                defaultRowsPerPage={rowMetaData.rowsPerPage}
                                headCells={headCells}
                                passRowMetaDataUp={setRowMetaData}
                                tableRows={createRows()}
                                totalResults={filteredRows.length}
                            />
                        )}
                    </div>
                    <div
                        className={`${local.formContent} ${
                            showForm ? local.showForm : local.hideForm
                        }`}>
                        {showForm ? (
                            formType === 'create' ? (
                                <AddDocument
                                    onClose={onClose}
                                    onCancel={onClose}
                                    isDisabled={isDisabled}
                                />
                            ) : (
                                <EditDocument
                                    isDisabled={isDisabled}
                                    row={selectedRow}
                                    onClose={() => setShowForm(false)}
                                />
                            )
                        ) : (
                            <Button
                                id="addDocumentButton"
                                disabled={isDisabled}
                                onClick={onAdd}
                                content="Add Document"
                            />
                        )}
                    </div>
                </div>
                {showAuditModal && (
                    <div id="auditDocumentModal">
                        <AuditDocumentModal
                            isOpen={showAuditModal}
                            label={auditLabel}
                            onClose={onAuditModalClose}
                        />
                    </div>
                )}
            </div>
            <div className={app.sideBar}>
                <ParticipantStickyMenu />
            </div>
        </div>
    );
};

export default Documents;
